/**
 * Persistent mini player at the bottom of every screen in a home, shown while
 * any visible media player there is playing. It sits in the layout below the
 * scroller (not over it), so content is never covered; --np-h lifts toasts.
 */
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useMatch } from 'react-router-dom';
import { Icon } from '../lib/icons.js';
import { useAccess, useEntity, useModel, useTicker } from '../lib/hooks.js';
import { MediaFeature, hasFeature } from '../lib/entity.js';
import { groupMembers, livePosition, media, mediaInfo, useNowPlaying } from '../lib/speakers.js';
import { haptic } from '../lib/native.js';
import { store } from '../lib/store.js';
import { Artwork } from '../components/music/Artwork.js';

const loadSheet = () => import('../components/music/NowPlayingSheet.js');
const NowPlayingSheet = lazy(loadSheet);

export function NowPlayingBar(): ReactNode {
  const match = useMatch('/h/:home/*');
  const home = match?.params.home;
  if (!home) return <BarSpace show={false} />;
  return <Bar key={home} home={home} />;
}

/** Publishes the bar height for toasts and anything else floating at the bottom. */
function BarSpace({ show, el }: { show: boolean; el?: HTMLElement | null }): null {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!show || !el) {
      root.style.removeProperty('--np-h');
      return;
    }
    const set = () => root.style.setProperty('--np-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--np-h');
    };
  }, [show, el]);
  return null;
}

function Bar({ home }: { home: string }): ReactNode {
  const np = useNowPlaying(home);
  const [open, setOpen] = useState(false);
  const [el, setEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (np.current) void loadSheet();
  }, [np.current]);
  return (
    <>
      <BarSpace show={!!np.current} el={el} />
      {np.current && <BarView home={home} id={np.current} all={np.all} onChoose={np.choose} onOpen={() => setOpen(true)} barRef={setEl} />}
      {(open || np.current) && (
        <Suspense fallback={null}>
          <NowPlayingSheet home={home} id={np.current} open={open && !!np.current} onClose={() => setOpen(false)} others={np.all} onSwitch={np.choose} />
        </Suspense>
      )}
    </>
  );
}

/** The bar itself; also the wall tablets' mini player (`large`: bigger art and touch targets, tablet.css). */
export function BarView({
  home, id, all, onChoose, onOpen, barRef, large = false,
}: {
  home: string;
  id: string;
  all: string[];
  onChoose: (id: string) => void;
  onOpen: () => void;
  barRef?: (el: HTMLElement | null) => void;
  large?: boolean;
}): ReactNode {
  const st = useEntity(home, id);
  const model = useModel(home);
  const access = useAccess(home);
  const m = mediaInfo(st);
  useTicker(1000, m.playing && m.duration !== null);
  const rooms = groupMembers(id, st).map((x) => model?.entities[x]?.name ?? x);
  const canCtl = !!access?.inWindow && !!access.entities[id]?.includes('control');
  const pos = livePosition(m);
  const progress = m.duration && pos !== null ? Math.min(100, (pos / m.duration) * 100) : null;
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const off = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false);
    };
    window.addEventListener('pointerdown', off);
    return () => window.removeEventListener('pointerdown', off);
  }, [menu]);
  const roomLabel = rooms.length > 2 ? `${rooms[0]} + ${rooms.length - 1}` : rooms.join(' + ');

  return (
    <div className={`np-dock${large ? ' np-dock-lg' : ''}`} ref={barRef} data-testid="np-bar">
      <div className="np-bar glass">
        {progress !== null && <span className="np-progress" style={{ width: `${progress}%` }} aria-hidden="true" />}
        <button type="button" className="np-open" onClick={onOpen} aria-label={`Now playing: ${m.title ?? 'music'} on ${rooms.join(', ')}. Open player`}>
          <Artwork src={m.art} size={large ? 64 : 44} />
          <span className="np-text">
            <span className="np-bar-title ellipsis">{m.title ?? (m.playing ? 'Playing' : 'Nothing playing')}</span>
            <span className="np-bar-sub ellipsis">
              <span className="np-rooms"><Icon name={rooms.length > 1 ? 'speaker-group' : 'speaker'} size={large ? 16 : 12} /> {roomLabel}</span>
              {m.artist && <> · {m.artist}</>}
            </span>
          </span>
        </button>
        {all.length > 1 && (
          <div className="np-switcher" ref={menuRef}>
            <button type="button" className="np-count" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((x) => !x)} title="Switch player" data-testid="np-switcher">
              {all.length}
            </button>
            {menu && (
              <div className="np-menu glass" role="menu">
                {all.map((p) => {
                  const pm = mediaInfoOf(home, p);
                  return (
                    <button
                      key={p}
                      type="button"
                      role="menuitemradio"
                      aria-checked={p === id}
                      onClick={() => {
                        onChoose(p);
                        setMenu(false);
                      }}
                    >
                      <Icon name={p === id ? 'check' : 'speaker'} size={15} />
                      <span className="stack">
                        <strong className="ellipsis">{model?.entities[p]?.name ?? p}</strong>
                        <span className="muted ellipsis small">{pm}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          className="icon-btn np-btn"
          aria-label={m.playing ? 'Pause' : 'Play'}
          disabled={!canCtl}
          onClick={() => {
            haptic('light');
            media.playPause(home, id, m.playing);
          }}
          data-testid="np-playpause"
        >
          <Icon name={m.playing ? 'pause' : 'play'} size={large ? 32 : 22} />
        </button>
        {hasFeature(st, MediaFeature.NEXT_TRACK) && (
          <button type="button" className="icon-btn np-btn np-next" aria-label="Next" disabled={!canCtl} onClick={() => media.next(home, id)}>
            <Icon name="next" size={large ? 30 : 20} />
          </button>
        )}
      </div>
    </div>
  );
}

/** One-line label for the switcher (not reactive: the menu is short-lived). */
function mediaInfoOf(home: string, id: string): string {
  const m = mediaInfo(store.entity(home, id));
  return [m.title, m.artist].filter(Boolean).join(' · ') || 'Playing';
}
